<template>
  <div class="header-rates" v-if="ratesData.length">
    <div class="header-rates__item" v-for="r in ratesData" :key="r.label">
      <span class="header-rates__pair">{{ r.label }}</span>
      <span
        class="header-rates__value"
        :class="{
          'header-rates__value--up': r.flash === 'up',
          'header-rates__value--down': r.flash === 'down'
        }"
        >{{ r.value }}</span
      >
    </div>
  </div>
</template>
<script>
import { dedupeValutesByKey } from "@/utils/valutes";
import { subscribeToLiveValutes, getLiveValutesState } from "@/utils/liveValutes";

// Пары для тикера в шапке — фиксированный, небольшой набор "основных
// направлений", запрошенный явно, не все валюты подряд (иначе не поместится
// и потеряет смысл как быстрый обзор).
const PAIRS = [
  { give: "BTC", get: "RUB" },
  { give: "BTC", get: "USDT" },
  { give: "LTC", get: "RUB" },
  { give: "USDT", get: "RUB" }
];

export default {
  name: "HeaderRates",
  data: () => ({
    // Не чистый computed от valutes — нужно хранить numericRate предыдущего
    // тика, чтобы на каждом обновлении понять, вырос курс или упал (для
    // подсветки), а computed для таких сравнений "предыдущее vs текущее" не
    // годится (не должен иметь состояния).
    ratesData: [], // [{ label, numericRate, value, flash: 'up'|'down'|null }]
    flashTimers: {},
    flashDurationMs: 900,
    unsubscribeValutes: null
  }),
  computed: {
    // Общее реактивное состояние из @/utils/liveValutes — раньше этот
    // компонент сам гонял getValutes() каждые 30 сек, теперь просто читает
    // то, что поддерживает актуальным один общий поллинг (см. liveValutes.js).
    liveValutes() {
      return getLiveValutesState().valutes;
    }
  },
  watch: {
    // Срабатывает и на тик общего поллинга, и на форс-рефреш из калькулятора
    // (например, когда пользователь его открывает) — тикер подхватывает
    // свежие курсы одинаково в обоих случаях.
    liveValutes: {
      handler(valutes) {
        this.applyRates(this.computeRates(valutes));
      },
      immediate: true
    }
  },
  methods: {
    // Масштаб курса сильно разный между парами (BTC/RUB — 7-значное целое,
    // BTC/USDT — 6-значное с копейками, LTC/RUB — 4-значное, USDT/RUB —
    // двузначное, где важны именно копейки) — фиксированная точность для
    // всех либо сыпала бы лишними нулями, либо обрезала значимые разряды.
    // minimumFractionDigits = maximumFractionDigits — иначе toLocaleString
    // молча обрезает хвостовые нули (85 вместо 85.00 для USDT/RUB).
    // Группировка разрядов (ru-RU) — для читаемости больших чисел.
    formatRate(rate) {
      const digits = rate >= 100 ? 0 : rate >= 1 ? 2 : 8;
      return rate.toLocaleString("ru-RU", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      });
    },
    computeRates(valutes) {
      const byKey = {};
      for (const v of dedupeValutesByKey(valutes)) byKey[v.key] = v;
      return PAIRS.map(p => {
        const give = byKey[p.give];
        const get = byKey[p.get];
        if (!give || !get || !give.course || !get.course) return null;
        return { label: `${p.give}/${p.get}`, numericRate: give.course / get.course };
      }).filter(Boolean);
    },
    // Сравнивает свежие курсы с уже отображаемыми (this.ratesData) и
    // проставляет flash — 'up'/'down' на пару секунд, как биржевой тикер.
    // На самой первой загрузке предыдущих значений ещё нет — не мигаем.
    applyRates(freshRates) {
      const prevByLabel = {};
      for (const r of this.ratesData) prevByLabel[r.label] = r;

      this.ratesData = freshRates.map(fresh => {
        const prev = prevByLabel[fresh.label];
        let flash = null;
        if (prev && prev.numericRate !== fresh.numericRate) {
          flash = fresh.numericRate > prev.numericRate ? "up" : "down";
        }
        return {
          label: fresh.label,
          numericRate: fresh.numericRate,
          value: this.formatRate(fresh.numericRate),
          flash
        };
      });

      this.ratesData.forEach(r => {
        if (!r.flash) return;
        // отдельный таймер на каждую пару — обновление одной не должно
        // гасить ещё не отыгравшую подсветку другой
        if (this.flashTimers[r.label]) clearTimeout(this.flashTimers[r.label]);
        this.flashTimers[r.label] = setTimeout(() => {
          const item = this.ratesData.find(x => x.label === r.label);
          if (item) item.flash = null;
        }, this.flashDurationMs);
      });
    },
  },
  mounted() {
    // Подписка сама запускает загрузку+поллинг, если это первый подписчик
    // за всё приложение — см. liveValutes.js.
    this.unsubscribeValutes = subscribeToLiveValutes();
  },
  beforeDestroy() {
    if (this.unsubscribeValutes) this.unsubscribeValutes();
    Object.values(this.flashTimers).forEach(clearTimeout);
  }
};
</script>
<style scoped>
.header-rates {
  position: absolute;
  right: 160px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 22px;
}
.header-rates__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.25;
}
.header-rates__pair {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.03em;
}
.header-rates__value {
  font-size: 0.92rem;
  font-weight: 600;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  transition: color 0.6s ease, text-shadow 0.6s ease;
}
.header-rates__value--up {
  color: #67c23a;
  text-shadow: 0 0 8px rgba(103, 194, 58, 0.65);
}
.header-rates__value--down {
  color: #f56c6c;
  text-shadow: 0 0 8px rgba(245, 108, 108, 0.65);
}
</style>



// WEBPACK FOOTER //
// src/components/ui/HeaderRates.vue